import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { SummaryBaseData } from "@/services/backend/htz/summary/types/base-data";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesSummaryBaseData = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "HtzSummaryBaseData" as const, id: request.id },
    { type: "HtzSummaryBaseData" as const, id: "LIST" },
  ] as const;

export const htzSummaryBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzSummaryBaseData", "DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzSummaryBaseDataCreate: build.mutation<SummaryBaseData, CreateRequest>({
        query: (request) => ({
          url: "/htz/summary/base-data",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "HtzSummaryBaseData", id: "LIST" }],
      }),
      htzSummaryBaseDataShow: build.query<SummaryBaseData, ShowRequest>({
        query: (request) => ({
          url: "/htz/summary/base-data",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzSummaryBaseData"),
      }),
      htzSummaryBaseDataList: build.query<Lst<SummaryBaseData>, ListRequest>({
        query: (request) => ({
          url: "/htz/summary/base-data/list",
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) =>
          providesList(result?.items, "HtzSummaryBaseData"),
      }),
      htzSummaryBaseDataPatch: build.mutation<SummaryBaseData, PatchRequest>({
        query: (request) => ({
          url: "/htz/summary/base-data",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesSummaryBaseData,
      }),
      htzSummaryBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/htz/summary/base-data",
          method: "DELETE",
          body: request,
        }),
        // A deleted instance can no longer be anyone's default, and the
        // backend refuses the delete while it still is — either way the
        // defaults have to be refetched.
        invalidatesTags: [
          { type: "HtzSummaryBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
    }),
  });

export const {
  useHtzSummaryBaseDataCreateMutation,
  useHtzSummaryBaseDataShowQuery,
  useHtzSummaryBaseDataListQuery,
  useHtzSummaryBaseDataPatchMutation,
  useHtzSummaryBaseDataDeleteMutation,
} = htzSummaryBaseDataApi;

// CreateRequest creates an instance. The label identifies it for the user
// and is unique per company; the defaults it holds are set afterwards
// with a PatchRequest.
export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  label: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  label?: Filter<string>;
  orderBy?: OrdrBy<SummaryBaseDataOrderField>[];
  limit?: number;
  offset?: number;
}

/** The columns the backend can order the base data by. */
export type SummaryBaseDataOrderField =
  | "id"
  | "label"
  | "createdAt"
  | "updatedAt";

// PatchRequest updates the instance. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged (the backend
// `patch.Field` is "unset" when the JSON key is absent). Renaming to a
// label another instance of the company holds is rejected.
export interface PatchRequest {
  id: UUID;
  label?: string;
  title?: string;
  subtitle?: string;
  coverLetter?: string;
}

export interface DeleteRequest {
  id: UUID;
}

/**
 * Patches the cached instance for an optimistic update. Defined here, at
 * module scope, so it keeps one identity for the whole app — see
 * {@link updateCache}.
 */
export const updateHtzSummaryBaseDataCache = updateCache(
  htzSummaryBaseDataApi,
  "htzSummaryBaseDataShow",
);
